import Close from "@mui/icons-material/Close";
import { Box, Divider, Drawer, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useId, useRef, useState } from "react";

export type RightDrawerShellWidth = { xs?: string; sm?: number; md?: number };

const DEFAULT_WIDTH: RightDrawerShellWidth = { xs: "90%", sm: 576, md: 672 };

export interface RightDrawerShellProps {
  open: boolean;
  onClose: () => void;
  /** Optional title shown on the left of the header row */
  title?: React.ReactNode;
  width?: RightDrawerShellWidth;
  /** Icon buttons rendered before the close control (e.g. download, edit) */
  headerActions?: React.ReactNode;
  /** Fixed content between header and scrollable body (e.g. file preview) */
  preview?: React.ReactNode;
  /** Pinned below the scrollable region (e.g. Save / Cancel) */
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** When false, drawer paper is empty (e.g. while parent data is loading) */
  showContent?: boolean;
  /** Default `true` — suppresses MUI's body scroll lock to avoid the
   *  scrollbar-width jitter on open/close. */
  disableScrollLock?: boolean;
  /**
   * Render a transparent (rather than dimmed) backdrop so the drawer feels like
   * an inspector panel. The backdrop still captures outside clicks and fires
   * `onClose`, so click-outside-to-close works either way.
   */
  hideBackdrop?: boolean;
  /**
   * Render as a persistent (non-modal) inspector panel: no backdrop at all, so
   * the page behind stays interactive — e.g. clicking another grid row
   * retargets the drawer instead of closing it. Esc and the X button still
   * close. Persistent-panel precedent: the customer Scout info panel.
   */
  nonModal?: boolean;
}

/**
 * Shared right-anchored MUI Drawer layout: header row (title + actions + close),
 * optional preview, scrollable body, optional footer.
 */
export function RightDrawerShell({
  open,
  onClose,
  title,
  width = DEFAULT_WIDTH,
  headerActions,
  preview,
  footer,
  children,
  showContent = true,
  disableScrollLock = true,
  hideBackdrop = false,
  nonModal = false,
}: RightDrawerShellProps) {
  const w = { ...DEFAULT_WIDTH, ...width };

  // A long title is truncated with an ellipsis (noWrap); surface the full text
  // in a tooltip, but only when it's actually clipped — measured on hover so no
  // tooltip appears for titles that fit. Only plain-string titles get this;
  // richer title nodes bring their own affordances.
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleClipped, setTitleClipped] = useState(false);
  const titleIsString = typeof title === "string";
  // The dialog is named by its title, so a screen reader announces what opened.
  const titleId = useId();
  const labelled = showContent && title != null && title !== "";

  // MUI only wires Esc-to-close for modal drawers; the persistent variant
  // needs it done by hand.
  useEffect(() => {
    if (!nonModal || !open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [nonModal, open, onClose]);

  return (
    <Drawer
      anchor="right"
      variant={nonModal ? "persistent" : "temporary"}
      open={open}
      onClose={onClose}
      disableScrollLock={disableScrollLock}
      slotProps={{
        ...(hideBackdrop ? { backdrop: { invisible: true } } : {}),
        paper: labelled ? { "aria-labelledby": titleId } : {},
      }}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: w.xs, sm: w.sm, md: w.md },
          boxSizing: "border-box",
          // Persistent papers render flat by default; keep the temporary
          // variant's elevation so the panel reads as an overlay.
          ...(nonModal ? { boxShadow: 16 } : {}),
        },
      }}
    >
      {showContent ? (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
            }}
          >
            {title != null && title !== "" ? (
              <Tooltip
                title={titleIsString && titleClipped ? title : ""}
                // Measured in onOpen rather than on hover: the Tooltip also
                // opens on keyboard focus, and a focus-first user would
                // otherwise get whatever the last hover measured (false, so an
                // empty tooltip) the first time they tab to a clipped title.
                onOpen={() => {
                  const el = titleRef.current;
                  if (el) setTitleClipped(el.scrollWidth > el.clientWidth);
                }}
              >
                <Typography
                  ref={titleRef}
                  id={titleId}
                  component="div"
                  variant="h6"
                  noWrap
                  tabIndex={titleIsString ? 0 : undefined}
                  sx={{ flex: 1, mr: 1, minWidth: 0 }}
                >
                  {title}
                </Typography>
              </Tooltip>
            ) : (
              <Box sx={{ flex: 1, mr: 1 }} />
            )}
            <Stack direction="row" spacing={0.5} alignItems="center">
              {headerActions}
              <Tooltip title="Close">
                <IconButton size="small" onClick={onClose} aria-label="Close drawer">
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Divider />

          {preview}
          {preview ? <Divider /> : null}

          <Box sx={{ flex: 1, overflow: "auto", p: 2, minHeight: 0 }}>{children}</Box>

          {footer ? (
            <>
              <Divider />
              <Box sx={{ flexShrink: 0, p: 2, bgcolor: "background.paper" }}>{footer}</Box>
            </>
          ) : null}
        </Box>
      ) : null}
    </Drawer>
  );
}
